import { parseMongoRawQuery } from '@/lib/mongo-raw-query'
import { normalizeExplorerLimit } from '@/lib/explorer-limit'
import { isIntegratedAccountExplorerType } from '@/lib/integrated-account-explorer'
import type { ExplorerFilter } from '@/composables/useDataExplorerRoute'
import type { ExplorerSort } from '@/lib/explorer-sort'
import { parseMongoFilterValueType, type MongoFilterValueType } from '@/lib/mongo-filter-value'

export type ExplorerAction = 'find' | 'count' | 'insert' | 'update' | 'delete'

export type VisualQueryPayload = {
  method: string
  args: Record<string, unknown>
}

export type VisualProjection = {
  action: ExplorerAction
  filters: ExplorerFilter[]
  /** Multi-column sort, in priority order. */
  sorts: ExplorerSort[]
  columns: string[]
  limit: string | null
  /** True when raw has features Visual cannot fully express. */
  visualPartial: boolean
  /** Insert document or update `$set` / update doc JSON (mutation actions). */
  mutationDocument?: string
}

const MONGO_OPS = new Set(['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'nin', 'exists', 'regex'])

function sqlLiteral(val: unknown): string {
  if (val === null) return 'NULL'
  if (typeof val === 'number') return String(val)
  if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE'
  return `'${String(val).replace(/'/g, "''")}'`
}

function sqlOperator(op: string): string {
  const map: Record<string, string> = {
    eq: '=',
    ne: '!=',
    gt: '>',
    gte: '>=',
    lt: '<',
    lte: '<=',
    like: 'LIKE',
  }
  return map[op] ?? op
}

function buildSqlRawFromVisualArgs(args: Record<string, unknown>): string {
  const table = args.table as string | undefined
  if (!table) {
    return [
      '-- Example: SELECT * FROM your_table WHERE id = 1',
      'SELECT * FROM your_table',
      'LIMIT 100',
    ].join('\n')
  }

  const cols = Array.isArray(args.select) && args.select.length
    ? (args.select as string[]).join(', ')
    : '*'
  let sql = `SELECT ${cols} FROM ${table}`

  const filter = args.filter as Record<string, unknown> | undefined
  if (filter && Object.keys(filter).length) {
    const clauses: string[] = []
    for (const [field, val] of Object.entries(filter)) {
      if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
        for (const [op, v] of Object.entries(val as Record<string, unknown>)) {
          clauses.push(`${field} ${sqlOperator(op)} ${sqlLiteral(v)}`)
        }
      } else {
        clauses.push(`${field} = ${sqlLiteral(val)}`)
      }
    }
    if (clauses.length) sql += `\nWHERE ${clauses.join(' AND ')}`
  }

  const orderBy = args.order_by as string | { column: string; direction?: string }[] | undefined
  if (typeof orderBy === 'string' && orderBy) {
    sql += `\nORDER BY ${orderBy}`
  } else if (Array.isArray(orderBy) && orderBy.length) {
    const parts = orderBy.map(o =>
      `${o.column}${o.direction?.toLowerCase() === 'desc' ? ' DESC' : ''}`,
    )
    sql += `\nORDER BY ${parts.join(', ')}`
  }

  if (typeof args.limit === 'number') sql += `\nLIMIT ${args.limit}`
  return sql
}

/** Build `SELECT COUNT(*) AS count FROM … WHERE …` from visual SQL find args. */
export function buildSqlCountFromVisualArgs(args: Record<string, unknown>): string {
  const table = args.table as string | undefined
  if (!table) return 'SELECT COUNT(*) AS count FROM your_table'

  let sql = `SELECT COUNT(*) AS count FROM ${table}`
  const filter = args.filter as Record<string, unknown> | undefined
  if (filter && Object.keys(filter).length) {
    const clauses: string[] = []
    for (const [field, val] of Object.entries(filter)) {
      if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
        for (const [op, v] of Object.entries(val as Record<string, unknown>)) {
          clauses.push(`${field} ${sqlOperator(op)} ${sqlLiteral(v)}`)
        }
      } else {
        clauses.push(`${field} = ${sqlLiteral(val)}`)
      }
    }
    if (clauses.length) sql += `\nWHERE ${clauses.join(' AND ')}`
  }
  return sql
}

export function visualArgsToRawQuery(
  connectorType: string,
  isSQL: boolean,
  visual: VisualQueryPayload,
): string {
  if (connectorType === 'mongodb') {
    if (visual.method === 'insert_one') {
      return JSON.stringify({ method: 'insert', data: visual.args.data ?? {} }, null, 2)
    }
    if (visual.method === 'update_many' || visual.method === 'update_one') {
      return JSON.stringify({
        method: 'update',
        filter: visual.args.filter ?? {},
        update: visual.args.update ?? { $set: {} },
      }, null, 2)
    }
    if (visual.method === 'delete_many' || visual.method === 'delete_one') {
      return JSON.stringify({ method: 'delete', filter: visual.args.filter ?? {} }, null, 2)
    }
    const method = visual.method === 'count' ? 'count' : 'find'
    const payload: Record<string, unknown> = { method }
    if (visual.args.filter) payload.filter = visual.args.filter
    if (method === 'find') {
      if (visual.args.projection) payload.projection = visual.args.projection
      if (visual.args.sort) payload.sort = visual.args.sort
      if (typeof visual.args.limit === 'number') payload.limit = visual.args.limit
    }
    return JSON.stringify(payload, null, 2)
  }

  if (connectorType === 'dynamics365') {
    // `top` always comes from the row-limit dropdown, so it alone shouldn't
    // force the JSON wrapper — a bare filter stays a plain OData string.
    const payload: Record<string, unknown> = {}
    if (visual.method === 'count') payload.method = 'count'
    if (typeof visual.args.filter === 'string' && visual.args.filter) {
      payload.filter = visual.args.filter
    }
    if (typeof visual.args.order_by === 'string' && visual.args.order_by) {
      payload.order_by = visual.args.order_by
    }
    if (typeof visual.args.select === 'string' && visual.args.select) {
      payload.select = visual.args.select
    }
    if (Object.keys(payload).length === 1 && typeof payload.filter === 'string') {
      return payload.filter as string
    }
    if (!Object.keys(payload).length) {
      return "statecode eq 0 and contains(name,'example')"
    }
    // JSON wrapper so sort/select/count survive Visual↔Raw round-trips.
    if (visual.method !== 'count' && typeof visual.args.top === 'number') {
      payload.top = visual.args.top
    }
    return JSON.stringify(payload, null, 2)
  }

  if (connectorType === 's3') {
    return JSON.stringify(
      { method: visual.method, args: visual.args },
      null,
      2,
    )
  }

  if (isIntegratedAccountExplorerType(connectorType)) {
    return JSON.stringify(
      { method: visual.method, args: visual.args },
      null,
      2,
    )
  }

  if (isSQL) {
    if (visual.method === 'count' || visual.method === 'select' && typeof visual.args.sql === 'string') {
      if (typeof visual.args.sql === 'string') return visual.args.sql
      return buildSqlCountFromVisualArgs(visual.args)
    }
    return buildSqlRawFromVisualArgs(visual.args)
  }

  return ''
}

export function patchLimitInRawQuery(
  connectorType: string,
  isSQL: boolean,
  raw: string,
  limit: number,
): string {
  if (connectorType === 'mongodb') {
    try {
      const parsed = JSON.parse(raw || '{}') as Record<string, unknown>
      parsed.limit = limit
      return JSON.stringify(parsed, null, 2)
    } catch {
      return raw
    }
  }

  if (isSQL) {
    const trimmed = raw.trimEnd()
    const withoutLimit = trimmed.replace(/\s+LIMIT\s+\d+\s*;?\s*$/i, '')
    return `${withoutLimit}\nLIMIT ${limit}`
  }

  if (isIntegratedAccountExplorerType(connectorType)) {
    try {
      const parsed = JSON.parse(raw || '{}') as { method?: string; args?: Record<string, unknown> }
      const args = { ...(parsed.args ?? {}) }
      const query = { ...((args.query as Record<string, unknown>) ?? {}) }
      query.limit = limit
      args.query = query
      return JSON.stringify({ ...parsed, args }, null, 2)
    } catch {
      return raw
    }
  }

  if (connectorType === 'dynamics365') {
    // Only the JSON wrapper carries `top`; plain OData filter strings take the
    // limit from the dropdown at run time and must not be rewritten into JSON.
    const trimmed = raw.trim()
    if (!trimmed.startsWith('{')) return raw
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>
      if (parsed.method === 'count') return raw
      parsed.top = limit
      return JSON.stringify(parsed, null, 2)
    } catch {
      return raw
    }
  }

  return raw
}

export function parseLimitFromRawQuery(
  connectorType: string,
  isSQL: boolean,
  raw: string,
): string | null {
  if (connectorType === 'mongodb') {
    try {
      const parsed = parseMongoRawQuery(raw)
      if (typeof parsed.limit === 'number') {
        return normalizeExplorerLimit(parsed.limit)
      }
    } catch {
      return null
    }
  }

  if (isSQL) {
    const match = raw.match(/\bLIMIT\s+(\d+)\s*;?\s*$/im)
    if (match) {
      return normalizeExplorerLimit(match[1])
    }
  }

  if (isIntegratedAccountExplorerType(connectorType)) {
    try {
      const parsed = JSON.parse(raw || '{}') as { args?: { query?: { limit?: unknown } } }
      const limit = parsed.args?.query?.limit
      if (typeof limit === 'number') {
        return normalizeExplorerLimit(limit)
      }
    } catch {
      return null
    }
  }

  if (connectorType === 'dynamics365') {
    const trimmed = raw.trim()
    if (!trimmed.startsWith('{')) return null
    try {
      const parsed = JSON.parse(trimmed) as { top?: unknown }
      if (typeof parsed.top === 'number') {
        return normalizeExplorerLimit(parsed.top)
      }
    } catch {
      return null
    }
  }

  return null
}

// ---
// Raw → Visual projection
// ---

function emptyProjection(overrides: Partial<VisualProjection> = {}): VisualProjection {
  return {
    action: 'find',
    filters: [],
    sorts: [],
    columns: [],
    limit: null,
    visualPartial: false,
    ...overrides,
  }
}

function inferMongoValueType(value: unknown): MongoFilterValueType {
  if (value === null) return 'null'
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') return 'number'
  if (typeof value === 'object' && value !== null && '$oid' in (value as object)) return 'objectId'
  if (typeof value === 'object' && value !== null && '$date' in (value as object)) return 'date'
  return 'auto'
}

function formatMongoFilterValue(value: unknown): string {
  if (value === null) return ''
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>
    if (typeof obj.$oid === 'string') return obj.$oid
    if (typeof obj.$date === 'string') return obj.$date
    return JSON.stringify(value)
  }
  return String(value)
}

function mongoClauseToFilter(field: string, value: unknown): { filter: ExplorerFilter | null; partial: boolean } {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    const ops = Object.keys(value as object)
    if (ops.length === 1) {
      const opKey = ops[0]
      const op = opKey.startsWith('$') ? opKey.slice(1) : opKey
      if (MONGO_OPS.has(op) || op === 'eq') {
        const v = (value as Record<string, unknown>)[opKey]
        return {
          filter: {
            field,
            operator: op === 'eq' ? 'eq' : op,
            value: formatMongoFilterValue(v),
            valueType: parseMongoFilterValueType(inferMongoValueType(v)),
          },
          partial: typeof v === 'object' && v !== null && !('$oid' in (v as object)) && !('$date' in (v as object)),
        }
      }
    }
    // Multi-op on one field (e.g. range) — take first, mark partial
    if (ops.length > 1) {
      const opKey = ops[0]
      const op = opKey.startsWith('$') ? opKey.slice(1) : 'eq'
      const v = (value as Record<string, unknown>)[opKey]
      return {
        filter: {
          field,
          operator: MONGO_OPS.has(op) ? op : 'eq',
          value: formatMongoFilterValue(v),
          valueType: parseMongoFilterValueType(inferMongoValueType(v)),
        },
        partial: true,
      }
    }
  }
  return {
    filter: {
      field,
      operator: 'eq',
      value: formatMongoFilterValue(value),
      valueType: parseMongoFilterValueType(inferMongoValueType(value)),
    },
    partial: false,
  }
}

function projectMongoFilter(filter: unknown): { filters: ExplorerFilter[]; partial: boolean } {
  if (!filter || typeof filter !== 'object' || Array.isArray(filter)) {
    return { filters: [], partial: !!filter }
  }
  const obj = filter as Record<string, unknown>
  let partial = false
  const filters: ExplorerFilter[] = []

  if (Array.isArray(obj.$and)) {
    for (const clause of obj.$and) {
      if (!clause || typeof clause !== 'object' || Array.isArray(clause)) {
        partial = true
        continue
      }
      const nested = projectMongoFilter(clause)
      filters.push(...nested.filters)
      if (nested.partial) partial = true
    }
    const otherKeys = Object.keys(obj).filter(k => k !== '$and')
    if (otherKeys.length) partial = true
    return { filters, partial }
  }

  if (obj.$or || obj.$expr || obj.$nor || obj.$where) {
    partial = true
  }

  for (const [field, value] of Object.entries(obj)) {
    if (field.startsWith('$')) {
      partial = true
      continue
    }
    const { filter: f, partial: p } = mongoClauseToFilter(field, value)
    if (f) filters.push(f)
    if (p) partial = true
  }
  return { filters, partial }
}

function projectMongoSort(sort: unknown): { sorts: ExplorerSort[]; partial: boolean } {
  if (!sort || typeof sort !== 'object' || Array.isArray(sort)) {
    return { sorts: [], partial: !!sort }
  }
  const sorts: ExplorerSort[] = []
  let partial = false
  for (const [field, dir] of Object.entries(sort as Record<string, unknown>)) {
    if (dir === 1 || dir === 'asc' || dir === 'ASC') {
      sorts.push({ column: field, order: 'asc' })
    } else if (dir === -1 || dir === 'desc' || dir === 'DESC') {
      sorts.push({ column: field, order: 'desc' })
    } else {
      // e.g. { $meta: "textScore" } — not expressible in Visual
      partial = true
    }
  }
  return { sorts, partial }
}

function projectMongoProjection(projection: unknown): { columns: string[]; partial: boolean } {
  if (!projection || typeof projection !== 'object' || Array.isArray(projection)) {
    return { columns: [], partial: !!projection }
  }
  const columns: string[] = []
  let partial = false
  for (const [field, val] of Object.entries(projection as Record<string, unknown>)) {
    if (val === 1 || val === true) columns.push(field)
    else if (val === 0 || val === false) partial = true
    else partial = true
  }
  return { columns, partial }
}

function projectMongoRaw(raw: string): VisualProjection {
  let parsed
  try {
    parsed = parseMongoRawQuery(raw)
  } catch {
    return emptyProjection({ visualPartial: true })
  }

  if (parsed.method === 'aggregate') {
    return emptyProjection({ visualPartial: true, action: 'find' })
  }

  if (parsed.method === 'insert') {
    return emptyProjection({
      action: 'insert',
      mutationDocument: JSON.stringify(parsed.data ?? {}, null, 2),
    })
  }

  if (parsed.method === 'update' || parsed.method === 'delete') {
    const { filters, partial: filterPartial } = projectMongoFilter(parsed.filter)
    return emptyProjection({
      action: parsed.method,
      filters,
      visualPartial: filterPartial,
      ...(parsed.method === 'update'
        ? { mutationDocument: JSON.stringify(parsed.update ?? { $set: {} }, null, 2) }
        : {}),
    })
  }

  const action: ExplorerAction = parsed.method === 'count' ? 'count' : 'find'
  const { filters, partial: filterPartial } = projectMongoFilter(parsed.filter)
  const { sorts, partial: sortPartial } = projectMongoSort(parsed.sort)
  const { columns, partial: projPartial } = projectMongoProjection(parsed.projection)
  const limit = typeof parsed.limit === 'number' ? normalizeExplorerLimit(parsed.limit) : null

  let visualPartial = filterPartial || sortPartial || projPartial
  if (parsed.pipeline) visualPartial = true
  if (typeof parsed.skip === 'number' && parsed.skip > 0) visualPartial = true

  return {
    action,
    filters,
    sorts: action === 'find' ? sorts : [],
    columns: action === 'find' ? columns : [],
    limit: action === 'find' ? limit : null,
    visualPartial,
  }
}

const SQL_OP_MAP: Record<string, string> = {
  '=': 'eq',
  '!=': 'ne',
  '<>': 'ne',
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
  LIKE: 'like',
}

function unquoteSqlLiteral(raw: string): string {
  const t = raw.trim()
  if ((t.startsWith("'") && t.endsWith("'")) || (t.startsWith('"') && t.endsWith('"'))) {
    return t.slice(1, -1).replace(/''/g, "'")
  }
  return t
}

function projectSqlWhere(where: string): { filters: ExplorerFilter[]; partial: boolean } {
  const filters: ExplorerFilter[] = []
  let partial = false
  // Split on AND only (OR / nested → partial)
  if (/\bOR\b/i.test(where) || where.includes('(')) {
    partial = true
  }
  const parts = where.split(/\s+AND\s+/i)
  for (const part of parts) {
    const m = part.trim().match(/^([A-Za-z_][\w.]*)\s*(=|!=|<>|>=|<=|>|<|LIKE)\s*(.+)$/i)
    if (!m) {
      partial = true
      continue
    }
    const op = SQL_OP_MAP[m[2].toUpperCase()] ?? SQL_OP_MAP[m[2]] ?? 'eq'
    filters.push({
      field: m[1],
      operator: op,
      value: unquoteSqlLiteral(m[3]),
    })
  }
  return { filters, partial }
}

function projectSqlOrderBy(clause: string): { sorts: ExplorerSort[]; partial: boolean } {
  const sorts: ExplorerSort[] = []
  let partial = false
  for (const part of clause.split(',')) {
    const m = part.trim().match(/^([A-Za-z_][\w.]*)(?:\s+(ASC|DESC))?$/i)
    if (!m) {
      partial = true
      continue
    }
    sorts.push({ column: m[1], order: m[2]?.toLowerCase() === 'desc' ? 'desc' : 'asc' })
  }
  return { sorts, partial }
}

function projectSqlRaw(raw: string): VisualProjection {
  const cleaned = raw.replace(/--[^\n]*/g, '').trim()
  const isCount = /^\s*SELECT\s+COUNT\s*\(\s*\*\s*\)/i.test(cleaned)
  const limitMatch = cleaned.match(/\bLIMIT\s+(\d+)\s*;?\s*$/im)
  const limit = limitMatch ? normalizeExplorerLimit(limitMatch[1]) : null

  const orderMatch = cleaned.match(/\bORDER\s+BY\s+(.+?)(?:\s+LIMIT\s+\d+|;|$)/is)
  const { sorts, partial: orderPartial } = orderMatch
    ? projectSqlOrderBy(orderMatch[1].trim())
    : { sorts: [], partial: false }

  const whereMatch = cleaned.match(/\bWHERE\s+(.+?)(?:\s+ORDER\s+BY|\s+LIMIT\s+\d+|$)/is)
  const { filters, partial: wherePartial } = whereMatch
    ? projectSqlWhere(whereMatch[1].trim())
    : { filters: [], partial: false }

  let visualPartial = wherePartial || orderPartial
  // JOINs, GROUP BY, subqueries, SELECT list other than * / COUNT(*)
  if (/\bJOIN\b/i.test(cleaned) || /\bGROUP\s+BY\b/i.test(cleaned) || cleaned.includes('(SELECT')) {
    visualPartial = true
  }
  if (!isCount) {
    const selectMatch = cleaned.match(/^\s*SELECT\s+(.+?)\s+FROM\b/is)
    if (selectMatch) {
      const sel = selectMatch[1].trim()
      if (sel !== '*') {
        const cols = sel.split(',').map(c => c.trim()).filter(Boolean)
        if (cols.some(c => /\s|\(|\)/.test(c))) visualPartial = true
        return {
          action: 'find',
          filters,
          sorts,
          columns: cols.every(c => /^[A-Za-z_][\w.]*$/.test(c)) ? cols : [],
          limit,
          visualPartial: visualPartial || cols.some(c => !/^[A-Za-z_][\w.]*$/.test(c)),
        }
      }
    } else {
      visualPartial = true
    }
  }

  return {
    action: isCount ? 'count' : 'find',
    filters,
    sorts: isCount ? [] : sorts,
    columns: [],
    limit: isCount ? null : limit,
    visualPartial,
  }
}

function projectDynamicsRaw(raw: string): VisualProjection {
  const trimmed = raw.trim()
  if (!trimmed) return emptyProjection()

  // Prefer JSON wrapper `{ filter, order_by, top, method }` when present
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>
      const filter = typeof parsed.filter === 'string' ? parsed.filter : ''
      const orderBy = typeof parsed.order_by === 'string' ? parsed.order_by : ''
      const top = typeof parsed.top === 'number' ? normalizeExplorerLimit(parsed.top) : null
      const select = typeof parsed.select === 'string' ? parsed.select : ''
      const columns = select
        ? select.split(',').map(c => c.trim()).filter(Boolean)
        : []
      const { sorts, partial: orderPartial } = projectDynamicsOrderBy(orderBy)
      const isCount = parsed.method === 'count'
      const proj = projectDynamicsFilter(filter)
      return {
        ...proj,
        action: isCount ? 'count' : 'find',
        sorts: isCount ? [] : sorts,
        columns: isCount ? [] : columns,
        limit: isCount ? null : top,
        visualPartial: proj.visualPartial || orderPartial,
      }
    } catch {
      // fall through to OData string
    }
  }

  return projectDynamicsFilter(trimmed)
}

/** OData `order_by` is comma-separated `field asc|desc` (direction optional). */
function projectDynamicsOrderBy(orderBy: string): { sorts: ExplorerSort[]; partial: boolean } {
  if (!orderBy.trim()) return { sorts: [], partial: false }
  const sorts: ExplorerSort[] = []
  let partial = false
  for (const part of orderBy.split(',')) {
    const m = part.trim().match(/^([A-Za-z_][\w.]*)(?:\s+(asc|desc))?$/i)
    if (!m) {
      partial = true
      continue
    }
    sorts.push({ column: m[1], order: m[2]?.toLowerCase() === 'desc' ? 'desc' : 'asc' })
  }
  return { sorts, partial }
}

function projectDynamicsFilter(filter: string): VisualProjection {
  if (!filter.trim()) return emptyProjection()
  // Best-effort: split on ` and `, map simple `field op value` or contains()
  if (/\bor\b/i.test(filter)) {
    return emptyProjection({
      filters: [{ field: '', operator: 'eq', value: filter }],
      visualPartial: true,
    })
  }
  const parts = filter.split(/\s+and\s+/i)
  const filters: ExplorerFilter[] = []
  let partial = false
  for (const part of parts) {
    const contains = part.trim().match(/^contains\(([A-Za-z_][\w.]*),\s*'([^']*)'\)$/i)
    if (contains) {
      filters.push({ field: contains[1], operator: 'contains', value: contains[2] })
      continue
    }
    const m = part.trim().match(/^([A-Za-z_][\w.]*)\s+(eq|ne|gt|ge|lt|le)\s+(.+)$/i)
    if (m) {
      // Keep OData operators (ge/le) — D365 visual builder uses those, not gte/lte.
      let value = m[3].trim()
      if ((value.startsWith("'") && value.endsWith("'")) || (value.startsWith('"') && value.endsWith('"'))) {
        value = value.slice(1, -1)
      }
      filters.push({
        field: m[1],
        operator: m[2].toLowerCase(),
        value,
      })
      continue
    }
    partial = true
  }
  if (!filters.length && filter.trim()) {
    return emptyProjection({
      filters: [{ field: '', operator: 'eq', value: filter }],
      visualPartial: true,
    })
  }
  return emptyProjection({ filters, visualPartial: partial })
}

/**
 * Best-effort projection of a raw query into Visual builder state.
 * Sets `visualPartial` when the raw query has features Visual cannot express.
 */
export function rawToVisualProjection(
  connectorType: string,
  isSQL: boolean,
  raw: string,
): VisualProjection {
  const trimmed = raw.trim()
  if (!trimmed) return emptyProjection()

  if (connectorType === 'mongodb') return projectMongoRaw(trimmed)
  if (isSQL) return projectSqlRaw(trimmed)
  if (connectorType === 'dynamics365') return projectDynamicsRaw(trimmed)

  return emptyProjection({ visualPartial: true })
}

export function parseExplorerAction(raw: unknown): ExplorerAction {
  const v = typeof raw === 'string' ? raw : ''
  if (v === 'count' || v === 'insert' || v === 'update' || v === 'delete') return v
  return 'find'
}
